import { Chip, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { MessageNotification } from "../../../_components/box/message-notification";
import { StandardPanel } from "../../../_components/panel/standard.panel";
import type { AnnualCatalogueItemRow } from "./auction-annual-shared";

interface AuctionAnnualCataloguePanelProps {
    loading: boolean;
    hasActiveCatalogue: boolean;
    catalogueItems: AnnualCatalogueItemRow[];
    pendingTurnItemIds: number[];
    committedItemIds: number[];
    occupiedItemIds?: Set<number>;
    isItemDisabled?: (item: AnnualCatalogueItemRow, itemId: number) => boolean;
    isItemTooLarge?: (item: AnnualCatalogueItemRow, itemId: number) => boolean;
    isItemBauLimited?: (item: AnnualCatalogueItemRow, itemId: number) => boolean;
    getItemBauRemaining?: (item: AnnualCatalogueItemRow, itemId: number) => number | null;
    onToggleItem: (itemId: number) => void;
}

export function AuctionAnnualCataloguePanel({
    loading,
    hasActiveCatalogue,
    catalogueItems,
    pendingTurnItemIds,
    committedItemIds,
    occupiedItemIds,
    isItemDisabled,
    isItemTooLarge,
    isItemBauLimited,
    getItemBauRemaining,
    onToggleItem,
}: AuctionAnnualCataloguePanelProps) {
    return (
        <StandardPanel title="Annual Catalogue Items" loading={loading} height="760px">
            {!loading && !hasActiveCatalogue ? (
                <MessageNotification message="No active catalogue found for this annual auction." />
            ) : !loading && catalogueItems.length === 0 ? (
                <MessageNotification message="No catalogue items found." />
            ) : (
                <List disablePadding>
                    {catalogueItems.map((item) => {
                        const itemId = Number(item.id ?? 0);
                        const isCommitted = committedItemIds.includes(itemId);
                        const isPending = pendingTurnItemIds.includes(itemId);
                        const isOccupiedByOther = !isPending && Boolean(occupiedItemIds?.has(itemId));
                        const tooLarge = Boolean(isItemTooLarge?.(item, itemId));
                        const bauLimited = Boolean(isItemBauLimited?.(item, itemId));
                        const bauRemaining = getItemBauRemaining?.(item, itemId);
                        const isTaken = isCommitted || isOccupiedByOther;
                        const disabled = isTaken || tooLarge || bauLimited || Boolean(isItemDisabled?.(item, itemId));

                        return (
                            <ListItem key={itemId} disablePadding sx={{ py: 0.5 }}>
                                <ListItemButton
                                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                                    disabled={disabled}
                                    selected={isPending}
                                    onClick={() => onToggleItem(itemId)}
                                >
                                    <ListItemText
                                        primary={`Lot ${item.lotNo ?? "—"} • €${item.price ?? "—"} • [${item.forestCode ?? "—"}] ${item.product ?? "—"}`}
                                        secondary={`District: ${item.district ?? "—"} • Volume: ${item.totalVolume ?? "—"}${bauRemaining != null ? ` • BAU left: ${bauRemaining}` : ""}`}
                                    />
                                    {isTaken ? (
                                        <Chip size="small" color="success" label="In Basket" />
                                    ) : isPending ? (
                                        <Chip size="small" color="primary" label="Selected" />
                                    ) : bauLimited ? (
                                        <Chip size="small" color="error" label="BAU Limit" />
                                    ) : tooLarge ? (
                                        <Chip size="small" color="warning" label="Too Large" />
                                    ) : (
                                        <Chip size="small" variant="outlined" label="Available" />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </StandardPanel>
    );
}
